// The Time Off kinds: jury duty, bereavement, unpaid, volunteer and vote, each a PTO request with
// a kind on top. The first three were decided 5 Oct 2026 with HR's answers, confirmed 9 Oct
// (docs/QUESTIONS.md, DECISION 9 Oct, item C); the spec is docs/TIME_OFF_PLAN.md. Volunteer and
// vote were added 12 Oct 2026 (PR 10), with HR's correction to vote the same day.
//
// What a kind changes, and where:
// - no balance check and never reserved (gates.ts, gate 3; projection.ts, reservedHours);
// - a limit for jury duty, bereavement and volunteer time, and vote is a fixed 2 h (gate 3a, here);
// - HR alone decides jury duty and bereavement (permissions.ts, decidedByHr here); volunteer
//   time and vote go to the supervisor like ordinary PTO;
// - only HR creates unpaid time off (the API's own entry point);
// - nobody outside the visibility rule learns the kind (visibility.ts, and requestLabel here).
import { mdyRange, toDayNumber } from './dates.js';
import { isLive, } from './types.js';
/** What everyone outside the visibility rule sees for ANY PTO, ordinary PTO included. */
export const TIME_OFF_LABEL = 'Time Off';
export const KIND_LABEL = {
    JURY_DUTY: 'Jury duty',
    BEREAVEMENT: 'Bereavement',
    UNPAID: 'Unpaid time off',
    VOLUNTEER: 'Volunteer',
    VOTE: 'Vote',
};
export const VOTE_WINDOW_LABEL = { START_OF_DAY: 'Start of day', END_OF_DAY: 'End of day' };
/**
 * The kinds HR decides, never the supervisor: jury duty and bereavement (TIME_OFF_PLAN), and
 * unpaid, which HR creates already decided. Volunteer and vote go to the supervisor (PR 10).
 * permissions.ts, the notification routing and the screens all ask this one question.
 */
const HR_DECIDES = new Set(['JURY_DUTY', 'BEREAVEMENT', 'UNPAID']);
export function decidedByHr(kind) {
    return !!kind && HR_DECIDES.has(kind);
}
/** No definitions until HR gives them (QC3). */
export const RELATIONSHIP_LABEL = {
    IMMEDIATE: 'Immediate family',
    EXTENDED: 'Extended family',
    OTHER: 'Someone else',
};
/** H1: per occurrence, no annual cap. Days are counted as 8 hours. */
export const BEREAVEMENT_LIMIT_HOURS = { IMMEDIATE: 40, EXTENDED: 16, OTHER: 8 };
/** H2: 10 days a year, per calendar year of each day (QC3). */
export const JURY_DUTY_HOURS_PER_YEAR = 80;
/** Volunteer time: up to 8 h in ANY 365 days, counted from live (pending or approved) volunteer requests (PR 10). */
export const VOLUNTEER_HOURS = 8;
export const VOLUNTEER_WINDOW_DAYS = 365;
/** Vote: 2 h, fixed, on one day, at the start or the end of it (HR, 12 Oct 2026). No yearly cap. */
export const VOTE_HOURS = 2;
const TYPE_LABEL = { PTO: 'PTO', NTD: 'No-travel', FIELD: 'Field work' };
/**
 * What a request is called on a screen. For PTO it depends on who is looking: somebody the
 * visibility rule admits sees the kind (or "PTO"), everyone else sees "Time Off", for every
 * PTO request, so "PTO" beside "Time Off" never gives a kind away. NTD and FIELD are unchanged.
 */
export function requestLabel(type, kind, mayRead) {
    if (type !== 'PTO')
        return TYPE_LABEL[type];
    if (!mayRead)
        return TIME_OFF_LABEL;
    return kind ? KIND_LABEL[kind] : TYPE_LABEL.PTO;
}
/**
 * How a request is named to people: its label and its dates, "PTO, 11/23/2026 – 11/25/2026", or
 * "Time Off, 11/23/2026 – 11/25/2026" outside the visibility rule. Never its number: that stays in the API and
 * the admin log.
 */
export function requestRef(label, start, end) {
    return `${label}, ${mdyRange(start, end)}`;
}
const EPSILON = 1e-9;
const yearOf = (date) => Number(date.slice(0, 4));
/**
 * The limits a jury-duty or bereavement request is checked against. Empty for every other
 * request.
 *
 * - Bereavement: the request's own hours against the relationship's limit. One request is one
 *   occurrence; HR approves every one, so HR is the check on two requests for one loss.
 * - Jury duty: per calendar year touched, the person's live jury duty that year (pending
 *   counts, cancelled and denied do not, and nor does anything in `exclude`: the request being
 *   evaluated and the one it supersedes) plus this request's days that year.
 */
export function kindAllowances(personId, kind, relationship, days, requests, exclude) {
    const total = days.reduce((s, d) => s + d.hours, 0);
    if (kind === 'BEREAVEMENT' && relationship) {
        return [{ kind, limitHours: BEREAVEMENT_LIMIT_HOURS[relationship], usedHours: 0, requestedHours: total, year: null }];
    }
    if (kind === 'VOLUNTEER')
        return volunteerAllowance(personId, days, requests, exclude);
    if (kind !== 'JURY_DUTY')
        return [];
    const asked = new Map();
    for (const d of days)
        asked.set(yearOf(d.date), (asked.get(yearOf(d.date)) ?? 0) + d.hours);
    const used = new Map();
    for (const r of requests) {
        if (r.type !== 'PTO' || r.timeOffKind !== 'JURY_DUTY' || !isLive(r) || exclude.has(r.id))
            continue;
        if (!r.resourceIds.includes(personId))
            continue;
        // Each day in its own year. Without the days (never, from the API's loader), the whole
        // request counts in the year it starts, which over-counts rather than under-counts.
        const rows = r.days ?? [{ date: r.start, hours: r.chargedHours }];
        for (const d of rows)
            used.set(yearOf(d.date), (used.get(yearOf(d.date)) ?? 0) + d.hours);
    }
    return [...asked].sort(([a], [b]) => a - b).map(([year, requestedHours]) => ({
        kind: 'JURY_DUTY', limitHours: JURY_DUTY_HOURS_PER_YEAR, usedHours: used.get(year) ?? 0, requestedHours, year,
    }));
}
/**
 * Volunteer time in a ROLLING 365 days: no window of 365 days in a row may hold more than 8 h of
 * volunteer time, this request included. Pending counts as well as approved, the same as jury duty
 * (decided 12 Oct 2026, replacing approved-only), so two pending requests cannot both be approved
 * past the cap. Cancelled and denied do not count, and nor does anything in `exclude`.
 *
 * Every window that holds a day of this request is checked; only windows ending on a day that
 * holds volunteer time can be the fullest, so those are the ones tried. The fullest is reported.
 */
function volunteerAllowance(personId, days, requests, exclude) {
    const asked = days.map(d => ({ n: toDayNumber(d.date), date: d.date, hours: d.hours }));
    if (!asked.length)
        return [];
    const held = [];
    for (const r of requests) {
        if (r.type !== 'PTO' || r.timeOffKind !== 'VOLUNTEER' || !isLive(r) || exclude.has(r.id))
            continue;
        if (!r.resourceIds.includes(personId))
            continue;
        for (const d of r.days ?? [{ date: r.start, hours: r.chargedHours }])
            held.push({ n: toDayNumber(d.date), date: d.date, hours: d.hours });
    }
    const span = VOLUNTEER_WINDOW_DAYS - 1;
    let best = null;
    for (const end of [...asked, ...held]) {
        if (!asked.some(a => a.n <= end.n && a.n >= end.n - span))
            continue;
        const inWindow = (d) => d.n <= end.n && d.n >= end.n - span;
        const usedHours = held.filter(inWindow).reduce((s, d) => s + d.hours, 0);
        const requestedHours = asked.filter(inWindow).reduce((s, d) => s + d.hours, 0);
        if (best && best.usedHours + best.requestedHours >= usedHours + requestedHours)
            continue;
        const from = held.concat(asked).filter(inWindow).map(d => d.date).sort()[0];
        best = { kind: 'VOLUNTEER', limitHours: VOLUNTEER_HOURS, usedHours, requestedHours, year: null, window: { from, through: end.date } };
    }
    return best ? [best] : [];
}
/** The first allowance this request goes past, as a sentence naming the limit and what is left. */
export function overLimit(allowances) {
    for (const a of allowances) {
        if (a.usedHours + a.requestedHours <= a.limitHours + EPSILON)
            continue;
        const h = (n) => `${Number(n.toFixed(1))} h`;
        if (a.kind === 'BEREAVEMENT') {
            return `Bereavement is up to ${h(a.limitHours)} (${a.limitHours / 8} ${a.limitHours === 8 ? 'day' : 'days'}) for this relationship, ` +
                `and this asks for ${h(a.requestedHours)}. Ask HR about the rest.`;
        }
        const left = Math.max(0, a.limitHours - a.usedHours);
        if (a.kind === 'VOLUNTEER') {
            return `Volunteer time is up to ${h(a.limitHours)} in any 365 days. ${h(a.usedHours)} is already requested or approved within 365 days ` +
                `of this, so ${h(left)} is left, and this asks for ${h(a.requestedHours)}. Ask HR about the rest.`;
        }
        return `Jury duty is capped at ${h(a.limitHours)} (10 days) in ${a.year}. ${h(left)} left that year, ` +
            `and this asks for ${h(a.requestedHours)}. Ask HR about the rest.`;
    }
    return null;
}
//# sourceMappingURL=timeoff.js.map